import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import {
  WebhookSubscriptionSchema,
  WebhookTestResultSchema,
  type CreateWebhookInput,
  type ExternalWebhookEventType,
  type UpdateWebhookInput,
  type WebhookSubscription,
  type WebhookTestResult,
} from '@shared/contracts/webhooks'
import { webhookKeys } from '@entities/webhook'

export async function createWebhook(input: CreateWebhookInput): Promise<WebhookSubscription> {
  return WebhookSubscriptionSchema.parse(
    await apiFetch<unknown>('/webhooks', { method: 'POST', body: input }),
  )
}

export async function updateWebhook(input: {
  webhookId: string
  values: UpdateWebhookInput
}): Promise<WebhookSubscription> {
  return WebhookSubscriptionSchema.parse(
    await apiFetch<unknown>(`/webhooks/${input.webhookId}`, {
      method: 'PATCH',
      body: input.values,
    }),
  )
}

export async function deleteWebhook(webhookId: string): Promise<void> {
  await apiFetch<unknown>(`/webhooks/${webhookId}`, { method: 'DELETE' })
}

export async function testWebhook(input: {
  webhookId: string
  eventType: ExternalWebhookEventType
}): Promise<WebhookTestResult> {
  return WebhookTestResultSchema.parse(
    await apiFetch<unknown>(`/webhooks/${input.webhookId}/test`, {
      method: 'POST',
      body: { eventType: input.eventType },
    }),
  )
}

export async function redeliverWebhookDelivery(input: {
  webhookId: string
  deliveryId: string
}): Promise<void> {
  await apiFetch<unknown>(`/webhooks/${input.webhookId}/deliveries/${input.deliveryId}/redeliver`, {
    method: 'POST',
  })
}

export async function rotateWebhookSecret(webhookId: string): Promise<WebhookSubscription> {
  return WebhookSubscriptionSchema.parse(
    await apiFetch<unknown>(`/webhooks/${webhookId}/rotate-secret`, { method: 'POST' }),
  )
}

export function useCreateWebhookMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createWebhook,
    onSuccess: (subscription) => {
      void queryClient.invalidateQueries({ queryKey: webhookKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: webhookKeys.detail(subscription.id) })
    },
  })
}

export function useUpdateWebhookMutation(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: UpdateWebhookInput) =>
      updateWebhook({ webhookId: webhookId ?? '', values }),
    onSuccess: (subscription) => {
      void queryClient.invalidateQueries({ queryKey: webhookKeys.lists() })
      void queryClient.setQueryData(webhookKeys.detail(subscription.id), subscription)
    },
  })
}

export function useDeleteWebhookMutation(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteWebhook(webhookId ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhookKeys.lists() })
    },
  })
}

export function useTestWebhookMutation(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventType: ExternalWebhookEventType) =>
      testWebhook({ webhookId: webhookId ?? '', eventType }),
    onSuccess: () => {
      if (webhookId) {
        void queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(webhookId) })
      }
    },
  })
}

export function useRedeliverWebhookDeliveryMutation(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (deliveryId: string) =>
      redeliverWebhookDelivery({ webhookId: webhookId ?? '', deliveryId }),
    onSuccess: () => {
      if (webhookId) {
        void queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(webhookId) })
      }
    },
  })
}

export function useRotateWebhookSecretMutation(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => rotateWebhookSecret(webhookId ?? ''),
    onSuccess: (subscription) => {
      void queryClient.invalidateQueries({ queryKey: webhookKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: webhookKeys.detail(subscription.id) })
    },
  })
}
